import type {
  PersonaDto,
  Platform as ApiPlatform,
  ScheduledPostAccountDto,
  ScheduledPostDto,
  SocialAccountDto,
} from '@reelplicate/api-client/model';

import type { Platform } from './platforms';

export type PostStatus =
  | 'scheduled'
  | 'published'
  | 'review'
  | 'draft'
  /** Its time has passed and it hasn't been found on some account yet. */
  | 'attention'
  | 'failed'
  | 'cancelled';

export const STATUS_LABELS: Record<PostStatus, string> = {
  scheduled: 'Scheduled',
  published: 'Published',
  review: 'Needs review',
  draft: 'Draft',
  attention: 'Not found yet',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

/** The status accent bar on post chips and month rows. */
export const STATUS_ACCENTS: Record<PostStatus, string> = {
  scheduled: 'bg-brand-primary',
  published: 'bg-status-success-solid',
  review: 'bg-status-warning-solid',
  draft: 'bg-border-strong',
  attention: 'bg-status-warning-solid',
  failed: 'bg-status-danger-solid',
  cancelled: 'bg-border-default',
};

/**
 * Where a post stands on one account. `checking` is its first minutes past
 * due, while the API looks for it on the platform; `missing` is past that and
 * still not found (it turns `failed` six hours after its time).
 */
export type AccountPostState =
  'draft' | 'upcoming' | 'checking' | 'missing' | 'published' | 'failed' | 'cancelled';

/** How long after its time a post counts as "being checked" before it's "not found yet". */
export const CHECK_GRACE_MS = 15 * 60_000;

export function accountPostState(
  account: Pick<ScheduledPostAccountDto, 'status'>,
  scheduledAt: Date,
  now: Date,
): AccountPostState {
  switch (account.status) {
    case 'PUBLISHED':
      return 'published';
    case 'FAILED':
      return 'failed';
    case 'CANCELLED':
      return 'cancelled';
    case 'DRAFT':
      return 'draft';
    case 'SCHEDULED': {
      const late = now.getTime() - scheduledAt.getTime();
      return late < 0 ? 'upcoming' : late < CHECK_GRACE_MS ? 'checking' : 'missing';
    }
  }
}

/** The brand or a persona, with the accounts that post for it. */
export type AccountGroup = {
  id: string;
  name: string;
  kind: 'brand' | 'persona';
  /** Short name on post chips ("O&E", "Bea"). */
  short: string;
  /** A persona's own time zone; null or absent uses the workspace's. */
  timezone?: string | null;
};

export type Account = {
  id: string;
  groupId: string;
  handle: string;
  initials: string;
  /** The persona's photo, or the workspace logo for the brand. */
  avatarUrl: string | null;
  platform: Platform;
};

export const BRAND_GROUP = 'brand';

/** The Calendar draws TikTok, Instagram, YouTube and Facebook; others wait for their marks. */
const PLATFORMS: Partial<Record<ApiPlatform, Platform>> = {
  TIKTOK: 'tiktok',
  INSTAGRAM: 'instagram',
  YOUTUBE: 'youtube',
  FACEBOOK: 'facebook',
};

/** The Calendar's mark for an API platform, when it draws one. */
export const toPlatform = (platform: ApiPlatform): Platform | undefined =>
  PLATFORMS[platform];

/** Every API platform's display name, including those without a mark. */
export const API_PLATFORM_NAMES: Record<ApiPlatform, string> = {
  TIKTOK: 'TikTok',
  INSTAGRAM: 'Instagram',
  YOUTUBE: 'YouTube',
  FACEBOOK: 'Facebook',
  LINKEDIN: 'LinkedIn',
};

/** "Oat & Ember" → "O&E". */
const shortOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => (word === '&' ? '&' : word[0]!.toUpperCase()))
    .join('');

/** "Oat & Ember" → "OE", "Barista Bea" → "BB". */
const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter((word) => /\p{L}/u.test(word))
    .slice(0, 2)
    .map((word) => word[0]!.toUpperCase())
    .join('');

/** The Accounts panel: the brand first, then personas with accounts. */
export function groupAccounts(
  brand: { name: string; logoUrl?: string | null },
  personas: PersonaDto[],
  accounts: SocialAccountDto[],
): { groups: AccountGroup[]; accounts: Account[] } {
  const brandName = brand.name;
  const avatars = new Map<string, string | null>([
    [BRAND_GROUP, brand.logoUrl ?? null],
    ...personas.map((p) => [p.id, p.avatarUrl] as const),
  ]);
  const groups: AccountGroup[] = [
    { id: BRAND_GROUP, name: brandName, kind: 'brand', short: shortOf(brandName) },
    ...personas.map((persona) => ({
      id: persona.id,
      name: persona.name,
      kind: 'persona' as const,
      short: persona.shortName ?? persona.name.split(/\s+/)[0]!,
      timezone: persona.timezone,
    })),
  ];
  const rows = accounts.flatMap((account) => {
    const platform = PLATFORMS[account.platform];
    const group = groups.find((g) => g.id === (account.personaId ?? BRAND_GROUP));
    if (!platform || !group) return [];
    return [
      {
        id: account.id,
        groupId: group.id,
        handle: account.handle,
        initials: initialsOf(group.name),
        avatarUrl: avatars.get(group.id) ?? null,
        platform,
      },
    ];
  });
  const used = new Set(rows.map((row) => row.groupId));
  return {
    groups: groups.filter((g) => g.kind === 'brand' || used.has(g.id)),
    accounts: rows,
  };
}

/** Worst first: what a chip's accent shows for a post on several accounts. */
const SEVERITY: AccountPostState[] = [
  'failed',
  'missing',
  'checking',
  'upcoming',
  'published',
  'cancelled',
];

/**
 * A post's state on the Calendar, from its accounts (only `accountIds` when
 * given - the ones shown): the worst of them wins, so a crosspost that failed
 * on one account reads as failed. Drafts stay drafts.
 */
export function postStatus(
  post: ScheduledPostDto,
  now: Date,
  accountIds?: ReadonlySet<string>,
): PostStatus {
  if (post.status === 'DRAFT') return 'draft';
  const at = new Date(post.scheduledAt);
  const accounts = accountIds
    ? post.accounts.filter((a) => accountIds.has(a.id))
    : post.accounts;
  const states = new Set(accounts.map((a) => accountPostState(a, at, now)));
  const worst = SEVERITY.find((state) => states.has(state));
  switch (worst) {
    case 'failed':
      return 'failed';
    case 'missing':
      return 'attention';
    case 'checking':
    case 'upcoming':
      return 'scheduled';
    case 'published':
      return 'published';
    case 'cancelled':
      return 'cancelled';
    default:
      // No accounts to go by: the post's own status.
      return post.status === 'PUBLISHED'
        ? 'published'
        : post.status === 'FAILED'
          ? 'failed'
          : post.status === 'CANCELLED'
            ? 'cancelled'
            : 'scheduled';
  }
}
